import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Card, CardContent, Spacer, Typography } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { Breadcrumbs } from '@mui/material';
import { AccreditationsPicker } from '@src/components/Admin/AccreditationsPicker/AccreditationsPicker';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { ClientPicker } from '@src/components/ClientPicker';
import { CountrySelect } from '@src/components/CountrySelect/CountrySelect';
import { COUNTRY } from '@src/graphql/queries/country';
import { COUNTRIES } from '@src/graphql/queries/lookup';
import { DELETE_SCHOOL, SAVE_SCHOOL, SCHOOLS, UPDATE_SCHOOL } from '@src/graphql/queries/school';
import { useAccreditations } from '@src/hooks/useAccreditations';
import { useAuth } from '@src/hooks/useAuth';
import { useSchools } from '@src/hooks/useSchools';
import { SchoolSource, SchoolType } from '@src/types';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { GlobalPermission } from '@src/utilities/permissions';
import { omit } from 'lodash';
import { Confirm, DataTable, EnumPicker, Form, Messages, SearchFilterBar, TrackableButton, useAlert, useLocale, } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styles from './SchoolsList.module.scss';
export const SchoolsList = () => {
    const { code: countryCode = '', id: accreditationId = '' } = useParams();
    const { hasPermission } = useAuth();
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { data: countryData } = useQuery(COUNTRY, { variables: { code: countryCode } });
    const country = countryData?.country;
    const { data: countriesData } = useQuery(COUNTRIES, { variables: { includeInactive: false } });
    // Parse and validate accreditation ID
    const accreditationIdNum = accreditationId ? parseInt(accreditationId) || undefined : undefined;
    // Only load accreditations if we're filtering by accreditation (lazy-loading)
    const { accreditations: allAccreditations } = useAccreditations({
        skip: !accreditationIdNum, // Skip query if not filtering by accreditation
    });
    const accreditation = accreditationIdNum
        ? allAccreditations.find(a => a.id === accreditationIdNum)
        : undefined;
    const [deleteSchool] = useMutation(DELETE_SCHOOL, {
        refetchQueries: (result) => {
            if (result.data?.deleteSchool?.success) {
                return [SCHOOLS];
            }
            return [];
        },
    });
    const [schoolToDelete, setSchoolToDelete] = useState(null);
    const refetchQueries = (result) => {
        if (!result.data)
            return [];
        if (result.data.saveSchool?.validationErrors || result.data.saveSchool?.error) {
            return [];
        }
        return [SCHOOLS];
    };
    const [searchText, setSearchText] = useState('');
    const [type, setType] = useState();
    const [source, setSource] = useState();
    const [accreditationIds, setAccreditationIds] = useState(accreditationIdNum ? [accreditationIdNum] : []);
    const [clientId, setClientId] = useState();
    const { schools, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = useSchools({
        countryCode,
        searchText,
        type,
        source,
        accreditationIds,
        clientId,
        includeInactive: true,
    });
    const [saveSchool] = useMutation(SAVE_SCHOOL, {
        refetchQueries,
    });
    // Reset pagination when search or filter values change
    useEffect(() => {
        setPage(1);
    }, [searchText, type, source, accreditationIds, clientId]);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [saveError, setSaveError] = useState('');
    const [messages, setMessages] = useState([]);
    const [update] = useMutation(UPDATE_SCHOOL);
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            // Only reset page if sort actually changed
            if (sort.field !== orderBy || sort.sort !== orderByDir) {
                setOrderBy(sort.field);
                setOrderByDir(sort.sort);
                setPage(1); // Reset to first page when sorting changes
            }
        }
        else {
            // Only reset page if we're clearing a sort that existed
            if (orderBy !== null || orderByDir !== null) {
                setOrderBy(null);
                setOrderByDir(null);
                setPage(1); // Reset to first page when sorting is cleared
            }
        }
    };
    const schoolTypeOptions = useMemo(() => Object.keys(SchoolType).map(type => ({
        value: type,
        label: t(`SchoolType.${type}`),
    })), [t]);
    // Helper to get country ID from country code
    const getCountryIdFromCode = (code) => {
        if (!code || !countriesData?.countries)
            return undefined;
        const country = countriesData.countries.find((c) => c.value === code);
        return country?.params?.id;
    };
    // Helper to get country code from country ID
    const getCountryCodeFromId = (id) => {
        if (!id || !countriesData?.countries)
            return '';
        const country = countriesData.countries.find((c) => c.params?.id === id);
        return country?.value || '';
    };
    const SchoolFields = useMemo(() => {
        const fields = [
            {
                name: 'name',
                label: t('School Name'),
                type: 'text',
            },
            {
                name: 'location',
                label: t('School Location'),
                type: 'text',
            },
            {
                name: 'type',
                label: t('School Type'),
                type: 'lookup',
                options: schoolTypeOptions,
            },
            {
                name: 'isActive',
                label: t('Active?'),
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
            {
                name: 'accredited',
                label: t('Accredited?'),
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
        ];
        return fields;
    }, [t, schoolTypeOptions]);
    const handleValidate = (school) => {
        const errors = {};
        if (!school.name) {
            errors.name = t('{field} is required', { values: { field: t('School Name') } });
        }
        if (!school.countryId) {
            errors.countryId = t('{field} is required', { values: { field: t('School Country') } });
        }
        if (school.accredited && !school.accreditationIds?.length) {
            errors.accreditationIds = t('At least one school grouping is required');
        }
        return errors;
    };
    const handleUpdateSearchResult = async (isActive) => {
        try {
            const result = await update({
                variables: {
                    input: {
                        isActive,
                    },
                    spec: {
                        clientId,
                        countryCode: country?.code,
                        name: searchText,
                        type,
                        source,
                        accreditationIds,
                        includeInactive: true,
                        pagination: {
                            page: 1,
                            pageSize: total,
                        },
                    },
                },
            });
            if (result.data?.updateSchool?.success) {
                await refetch();
                const message = result.data.updateSchool.messages[0];
                // If count is available in the payload, include it in the message
                const count = result.data.updateSchool.payload?.count;
                const toShow = count
                    ? t(message.value, { values: { ...message.params, count } })
                    : t(message.value, { values: message.params });
                showAlert(toShow, 'success');
            }
            else {
                const message = result.data.updateSchool.messages[0];
                const toShow = t(message.value, { values: message.params });
                showAlert(toShow, 'error');
            }
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Unable to update schools'));
        }
    };
    const handleSave = async (school) => {
        let result;
        try {
            setSaveError('');
            result = await saveSchool({
                variables: {
                    input: {
                        ...omit(prepareForSave(school), 'accreditations', 'countryCode', 'countryName'),
                        accreditationIds: school.accredited ? (school.accreditationIds || []).map(Number) : [],
                    },
                },
            });
            if (result.data) {
                if (result.data.saveSchool?.validationErrors) {
                    return result.data.saveSchool?.validationErrors;
                }
                if (result.data.saveError?.error) {
                    setSaveError(t(result.data.saveError.error));
                    return;
                }
            }
            showAlert(t('School saved'));
            setSelectedSchool(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const msg = e.message;
            setSaveError(msg);
        }
    };
    const handleAccreditationsChange = (setDraft) => (accreditationIds) => {
        setDraft((prev) => ({
            ...prev,
            accreditationIds,
        }));
    };
    const handleClientIdChange = (setDraft) => (clientId) => {
        setDraft((prev) => ({
            ...prev,
            clientId,
        }));
    };
    const handleCountryChange = (setDraft) => (countryCode) => {
        const countryId = getCountryIdFromCode(countryCode);
        setDraft((prev) => ({
            ...prev,
            countryId: countryId || 0,
            countryCode,
        }));
    };
    const handleFilterReset = () => {
        setSearchText('');
        setType(undefined);
        setSource(undefined);
        setAccreditationIds([]);
        setClientId(undefined);
        setPage(1);
    };
    const handleDelete = async () => {
        if (!schoolToDelete)
            return;
        const result = await deleteSchool({ variables: { id: schoolToDelete.id } });
        if (result.data?.deleteSchool?.success) {
            showAlert(t('School deleted'));
        }
        else {
            setMessages(result.data?.deleteSchool?.messages || []);
        }
        setSchoolToDelete(null);
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Schools'), navLinks: [
                    {
                        title: t('Countries'),
                        path: '/admin/schools/countries',
                        selected: !!countryCode && !accreditationIdNum,
                    },
                    {
                        title: t('Groups'),
                        path: '/admin/schools/accreditations',
                        selected: !!accreditationIdNum && !countryCode,
                    },
                ], searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, enableFilter: true, searchText: searchText, searchPlaceholder: t('Search by Name'), onSearchChange: searchText => {
                        setSearchText(searchText);
                        setPage(1);
                    }, filterActions: _jsxs("div", { className: styles.filter, children: [_jsx("div", { className: styles.clientPicker, children: _jsx(ClientPicker, { clientId: clientId, onChange: clientId => {
                                        setClientId(clientId);
                                        setPage(1);
                                    } }) }), _jsx("div", { className: styles.schoolType, children: _jsx(EnumPicker, { label: t('School Type'), labelPrefix: 'SchoolType', enumType: SchoolType, value: type || '', onChange: e => {
                                        setType(e);
                                        setPage(1);
                                    } }) }), _jsx("div", { className: styles.schoolSource, children: _jsx(EnumPicker, { label: t('Source'), labelPrefix: 'SchoolSource', enumType: SchoolSource, value: source || '', onChange: e => {
                                        setSource(e);
                                        setPage(1);
                                    } }) }), _jsx("div", { className: styles.accreditations, children: _jsx(AccreditationsPicker, { className: styles.accreditations, accreditationIds: accreditationIds, onChange: ids => {
                                        setAccreditationIds(ids);
                                        setPage(1);
                                    } }) }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Reset', tag: 'header', variant: 'outlined', onClick: handleFilterReset, children: t('Reset') })] }), isFilterActive: !!(clientId || type || source || accreditationIds.length) }), actions: hasPermission(GlobalPermission.CREATE_SCHOOL) && (_jsx(TrackableButton, { btnType: 'mui', enText: 'Add School', tag: 'header', variant: 'contained', onClick: () => setSelectedSchool({
                        id: 0,
                        countryId: country?.id ?? 0,
                        countryCode: country?.code,
                        name: '',
                        location: '',
                        type: SchoolType.PUBLIC,
                        accredited: false,
                        accreditationIds: [],
                        isActive: true,
                    }), children: t('Add School') })) }), _jsxs("div", { children: [countryCode && country && !accreditationIdNum && (_jsx("div", { className: 'mb-4', children: _jsxs(Breadcrumbs, { sx: {
                                '& li': {
                                    color: 'var(--admin-topnav-title-color)',
                                    a: { color: 'var(--admin-primary-color)' },
                                },
                            }, children: [_jsx(Link, { to: '/admin/schools/countries', children: t('Countries') }), _jsx("span", { children: country.name })] }) })), accreditationIdNum && accreditation && !countryCode && (_jsx("div", { className: 'mb-4', children: _jsxs(Breadcrumbs, { sx: {
                                '& li': {
                                    color: 'var(--admin-topnav-title-color)',
                                    a: { color: 'var(--admin-primary-color)' },
                                },
                            }, children: [_jsx(Link, { to: '/admin/schools/accreditations', children: t('Groups') }), _jsx("span", { children: accreditation.name })] }) })), _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), total > 0 && !loading && hasPermission(GlobalPermission.UPDATE_SCHOOL) && (_jsx("div", { children: _jsxs("div", { className: 'mb-3 flex gap-1', children: [_jsx(TrackableButton, { buttonSize: 'small', appearance: 'outline', enText: 'Make Inactive', tag: 'school_search', onClick: () => {
                                                    handleUpdateSearchResult(false);
                                                }, children: t('Make {count} Inactive', {
                                                    values: { count: total },
                                                }) }), _jsx(TrackableButton, { buttonSize: 'small', appearance: 'outline', enText: 'Make Active', tag: 'school_search', onClick: () => {
                                                    handleUpdateSearchResult(true);
                                                }, children: t('Make {count} Active', {
                                                    values: { count: total },
                                                }) })] }) })), _jsx(Messages, { messages: messages, className: 'mb-4', onClose: () => setMessages([]) }), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: schools, loading: loading, getRowHeight: params => {
                                        return params.model.accreditations.length > 2 ? 'auto' : undefined;
                                    }, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                        setPage(pagination.page + 1);
                                        setPageSize(pagination.pageSize);
                                    }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'name', sort: orderByDir }], rowCount: total, columns: [
                                        {
                                            field: 'name',
                                            headerName: t('School Name'),
                                            flex: 0.2,
                                        },
                                        {
                                            field: 'location',
                                            headerName: t('School Location'),
                                            flex: 0.2,
                                        },
                                        // Only show School Country column when viewing from Groups page
                                        ...(accreditationIdNum && !countryCode
                                            ? [
                                                {
                                                    field: 'countryName',
                                                    headerName: t('School Country'),
                                                    flex: 0.2,
                                                    renderCell: ({ row }) => row.countryName || '',
                                                },
                                            ]
                                            : []),
                                        {
                                            field: 'type',
                                            headerName: t('School Type'),
                                            flex: 0.2,
                                            renderCell: ({ value }) => t(`SchoolType.${value}`),
                                        },
                                        {
                                            field: 'accredited',
                                            headerName: t('Accredited?'),
                                            width: 150,
                                            renderCell: ({ row }) => (row.accredited ? t('Yes') : t('No')),
                                        },
                                        {
                                            field: 'accreditations',
                                            headerName: t('Groups'),
                                            flex: 0.2,
                                            renderCell: ({ row }) => (row.accreditations || []).map((acc) => acc.name).join(', '),
                                        },
                                        {
                                            field: 'clientName',
                                            headerName: t('Client'),
                                            flex: 0.2,
                                            sortable: true,
                                        },
                                        {
                                            field: 'source',
                                            headerName: t('Source'),
                                            flex: 0.2,
                                            renderCell: ({ value }) => (value ? t(`SchoolSource.${value}`) : ''),
                                        },
                                        {
                                            field: 'isActive',
                                            headerName: t('Active?'),
                                            width: 100,
                                            renderCell: ({ value }) => (value ? t('Yes') : t('No')),
                                        },
                                        {
                                            field: 'action',
                                            headerName: t('Actions'),
                                            renderCell: ({ row }) => (_jsx("div", { children: hasPermission(GlobalPermission.CREATE_SCHOOL) && (_jsxs(_Fragment, { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `school_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedSchool(row), className: 'margin--right_8' }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `school_${row.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => setSchoolToDelete(row), className: 'margin--right_8' })] })) })),
                                            width: 100,
                                            sortable: false,
                                        },
                                    ] }), selectedSchool && (_jsx(Form, { title: selectedSchool.id ? 'Edit School' : 'Add School', modal: true, obj: {
                                        ...selectedSchool,
                                        location: selectedSchool.location || '',
                                        countryCode: selectedSchool.countryCode || getCountryCodeFromId(selectedSchool.countryId),
                                    }, fields: SchoolFields, onValidate: handleValidate, onSave: handleSave, onCancel: () => setSelectedSchool(null), error: saveError, children: ({ draft, setDraft, errors }) => (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'label1', children: t('School Country') }), _jsx(CountrySelect, { label: t('School Country'), value: getCountryCodeFromId(draft.countryId), onChange: handleCountryChange(setDraft), errors: errors.countryId, options: countriesData?.countries || [] }), _jsx(Spacer, { marginBottom: 8 }), _jsx(ClientPicker, { clientId: draft.clientId, onChange: handleClientIdChange(setDraft) }), draft.accredited && (_jsx(AccreditationsPicker, { className: styles.accreditations, accreditationIds: draft.accreditationIds || [], onChange: handleAccreditationsChange(setDraft), errors: errors.accreditationIds }))] })) })), schoolToDelete && (_jsx(Confirm, { message: t('Are you sure you want to delete school {name}?', {
                                        values: { name: schoolToDelete.name },
                                    }), onConfirm: handleDelete, onCancel: () => setSchoolToDelete(null) }))] }) })] })] }));
};
